import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Badge,
  HStack,
  VStack,
  IconButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton,
  Alert,
  AlertIcon,
  Avatar,
  AvatarGroup,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiMoreVertical, 
  FiUsers, 
  FiMic, 
  FiMicOff, 
  FiSettings,
  FiVolume2,
  FiVolumeX,
  FiLogIn,
  FiLogOut,
  FiHeadphones
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useVoiceService, VoiceGroup, VoiceRoom } from '../../hooks/useVoiceService';
import { useVoiceStatusStream } from '../../hooks/useVoiceStatusStream';
import { CreateGroupModal } from './components/CreateGroupModal';

const getGroupTypeLabel = (type: number) => {
  switch (type) {
    case 1: return 'Text';
    case 2: return 'Voice';
    case 3: return 'Video';
    case 4: return 'Streaming';
    default: return 'Unknown';
  }
};

const getGroupStatusLabel = (status: number) => {
  switch (status) {
    case 1: return 'Active';
    case 2: return 'Inactive';
    case 3: return 'Archived';
    case 4: return 'Locked';
    default: return 'Unknown';
  }
};

const getGroupStatusColor = (status: number) => {
  switch (status) {
    case 1: return 'green';
    case 2: return 'gray';
    case 3: return 'orange';
    case 4: return 'red';
    default: return 'gray';
  }
};

const getMemberRoleLabel = (role: number) => {
  switch (role) {
    case 1: return 'Member';
    case 2: return 'Moderator';
    case 3: return 'Admin';
    case 4: return 'Owner';
    default: return 'Guest';
  }
};

const getRoomTypeLabel = (type: number) => {
  switch (type) {
    case 1: return 'Standard';
    case 2: return 'Workspace';
    case 3: return 'Event';
    case 4: return 'Gaming';
    default: return 'Unknown';
  }
};

const getRoomStatusLabel = (status: number) => {
  switch (status) {
    case 1: return 'Active';
    case 2: return 'Inactive';
    case 3: return 'Archived';
    case 4: return 'Locked';
    default: return 'Unknown';
  }
};

const getRoomStatusColor = (status: number) => {
  switch (status) {
    case 1: return 'green';
    case 2: return 'gray';
    case 3: return 'orange';
    case 4: return 'red';
    default: return 'gray';
  }
};

export const VoiceGroupsPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const [room, setRoom] = useState<VoiceRoom | null>(null);
  const [groups, setGroups] = useState<VoiceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { 
    getRoom,
    getVoiceGroups, 
    joinVoiceGroup, 
    leaveVoiceGroup,
    muteMember,
    unmuteMember 
  } = useVoiceService();

  // Get group IDs for status streaming
  const groupIds = groups.map(group => group.id);
  const {
    groupStatusUpdates,
    isConnected,
    getLatestGroupStatus
  } = useVoiceStatusStream({
    roomIds: roomId ? [roomId] : [],
    groupIds,
    includeInitialState: true,
    enabled: groupIds.length > 0,
  });

  const loadRoomAndGroups = useCallback(async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load room info and groups in parallel
      const [roomResponse, groupsResponse] = await Promise.all([
        getRoom(roomId),
        getVoiceGroups(roomId)
      ]);
      
      setRoom(roomResponse.room || null);
      setGroups(groupsResponse.voice_groups || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load room and voice groups');
    } finally {
      setLoading(false);
    }
  }, [getRoom, getVoiceGroups, roomId]);

  useEffect(() => {
    loadRoomAndGroups();
  }, [loadRoomAndGroups]);

  // Update group statuses based on real-time updates
  useEffect(() => {
    if (groupStatusUpdates.length === 0) return;

    setGroups(prevGroups => {
      const updatedGroups = [...prevGroups];
      
      groupStatusUpdates.forEach(update => {
        const groupIndex = updatedGroups.findIndex(group => group.id === update.group_id);
        if (groupIndex !== -1) {
          updatedGroups[groupIndex] = {
            ...updatedGroups[groupIndex],
            ...update.group,
            status: update.status,
          };
        }
      });
      
      return updatedGroups;
    });
  }, [groupStatusUpdates]);

  const handleJoinGroup = async (groupId: string) => {
    try {
      await joinVoiceGroup(groupId);
      await loadRoomAndGroups();
    } catch (err) {
      console.error('Failed to join group:', err);
    }
  };

  const handleLeaveGroup = async (groupId: string) => {
    try {
      await leaveVoiceGroup(groupId);
      await loadRoomAndGroups();
    } catch (err) {
      console.error('Failed to leave group:', err);
    }
  };

  const handleGroupCreated = () => {
    onClose();
    loadRoomAndGroups();
  };

  // Update left sidebar with group navigation and actions
  useEffect(() => {
    const sidebarItems = [ 
      {
        label: 'All Rooms',
        path: '/voice/rooms',
      },
      {
        label: room?.name || 'Room Groups',
        path: `/voice/rooms/${roomId}`,
        active: true,
      },
    ];

    // Add create group action if we have room info
    if (room) {
      sidebarItems.push({
        label: 'Create Group',
        path: '#',
        action: () => onOpen(),
        icon: 'FiPlus',
        style: 'primary'
      } as any);
    }

    window.updateLeftSidebar?.(sidebarItems);

    return () => window.updateLeftSidebar?.([]);
  }, [roomId, room?.name, room, onOpen]);

  // Update right sidebar with group actions
  useEffect(() => {
    window.updateRightSidebar?.(
      <VStack spacing={4}>
        <Button 
          leftIcon={<FiPlus />} 
          colorScheme="blue" 
          onClick={onOpen}
          width="full"
          isDisabled={!roomId}
        >
          Create Group
        </Button>
        
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Connection Status
          </Text>
          <Badge colorScheme={isConnected ? 'green' : 'red'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
        </Box>

        {room && (
          <VStack align="stretch" spacing={2} width="full">
            <Text fontSize="sm" fontWeight="medium">
              Room Info
            </Text>
            
            <VStack align="stretch" spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Max Groups: {room.settings?.max_groups || 'N/A'}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Auto Archive: {room.settings?.auto_archive_days || 'N/A'} days
              </Text>
              <Text fontSize="xs" color="gray.500">
                Max Members: {room.settings?.default_group_settings?.max_members || 'N/A'}
              </Text>
            </VStack>
          </VStack>
        )}
      </VStack>
    );

    return () => window.updateRightSidebar?.(null);
  }, [onOpen, isConnected, roomId, room, navigate]);

  if (!roomId) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Room ID is required
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={6} />
        <Grid templateColumns="repeat(auto-fill, minmax(350px, 1fr))" gap={6}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height="250px" />
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {room && (
        <Card mb={6}>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Heading size="lg">{room.name}</Heading>
                  <HStack>
                    <Badge colorScheme="blue" variant="subtle">
                      {getRoomTypeLabel(room.type)}
                    </Badge>
                    <Badge colorScheme={getRoomStatusColor(room.status)} variant="subtle">
                      {getRoomStatusLabel(room.status)}
                    </Badge>
                  </HStack>
                </VStack>
                <HStack>
                  <Button
                    size="sm"
                    leftIcon={<FiSettings />}
                    variant="outline"
                    onClick={() => navigate(`/voice/rooms/${roomId}/settings`)}
                  >
                    Room Settings
                  </Button>
                </HStack>
              </HStack>
              
              {room.description && (
                <Text color="gray.600">{room.description}</Text>
              )}
              
              <HStack>
                <HStack>
                  <FiUsers />
                  <Text fontSize="sm">
                    {room.groups_count || 0} group{(room.groups_count || 0) !== 1 ? 's' : ''}
                  </Text>
                </HStack>
                <HStack>
                  {room.settings?.is_public ? <FiMic /> : <FiMicOff />}
                  <Text fontSize="sm">
                    {room.settings?.is_public ? 'Public' : 'Private'}
                  </Text>
                </HStack>
                <Text fontSize="sm" color="gray.500">
                  Created: {room.created_at ? new Date(room.created_at).toLocaleDateString() : 'Unknown'}
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Voice Groups</Heading>
        <HStack>
          <Text fontSize="sm" color="gray.500">
            {groups.length} group{groups.length !== 1 ? 's' : ''}
          </Text>
          {isConnected && (
            <Badge colorScheme="green" variant="subtle">
              Live Updates
            </Badge>
          )}
        </HStack>
      </HStack>

      {groups.length === 0 ? (
        <Card>
          <CardBody>
            <VStack spacing={4} py={8}>
              <FiUsers size={48} color="gray" />
              <Text color="gray.500">No voice groups in this room yet</Text>
              <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>
                Create First Group
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(400px, 1fr))" gap={6}>
          {groups.map((group) => {
            const latestStatus = getLatestGroupStatus(group.id);
            const currentStatus = latestStatus?.status ?? group.status;
            const memberCount = group.members?.members?.length || 0;
            const maxMembers = group.settings?.max_members || 20;
            const occupancyRate = (memberCount / maxMembers) * 100;
            
            return (
              <Card 
                key={group.id}
                cursor="pointer"
                _hover={{ shadow: 'md' }}
                onClick={() => navigate(`/voice/groups/${group.id}`)}
              >
                <CardHeader pb={2}>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack>
                        <Text fontWeight="bold" noOfLines={1}>
                          {group.name}
                        </Text>
                        {group.is_default && (
                          <Badge colorScheme="purple" variant="subtle" size="sm">
                            Default
                          </Badge>
                        )}
                      </HStack>
                      <HStack>
                        <Badge colorScheme="blue" variant="subtle">
                          {getGroupTypeLabel(group.type)}
                        </Badge>
                        <Badge colorScheme={getGroupStatusColor(currentStatus)} variant="subtle">
                          {getGroupStatusLabel(currentStatus)}
                        </Badge>
                      </HStack>
                    </VStack>
                    
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MenuList>
                        <MenuItem 
                          icon={<FiLogIn />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJoinGroup(group.id);
                          }}
                        >
                          Join Group
                        </MenuItem>
                        <MenuItem 
                          icon={<FiLogOut />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeaveGroup(group.id);
                          }}
                        >
                          Leave Group
                        </MenuItem>
                        <MenuItem 
                          icon={<FiSettings />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/voice/groups/${group.id}/settings`);
                          }}
                        >
                          Settings
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </CardHeader>
                
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={4}>
                    {group.description && (
                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {group.description}
                      </Text>
                    )}
                    
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" fontWeight="medium">
                          Members ({memberCount}/{maxMembers})
                        </Text>
                        <HStack>
                          {group.settings?.is_public ? <FiMic /> : <FiMicOff />}
                          <Text fontSize="sm">
                            {group.settings?.is_public ? 'Public' : 'Private'}
                          </Text>
                        </HStack>
                      </HStack>
                      
                      <Progress 
                        value={occupancyRate} 
                        colorScheme={occupancyRate > 80 ? 'red' : occupancyRate > 60 ? 'yellow' : 'green'}
                        size="sm"
                        hasStripe
                      />
                    </VStack>
                    
                    {group.members?.members && group.members.members.length > 0 && (
                      <VStack align="stretch" spacing={2}>
                        <Text fontSize="sm" fontWeight="medium">Active Members</Text>
                        <HStack>
                          <AvatarGroup size="sm" max={5}>
                            {group.members.members.slice(0, 8).map((member, index) => (
                              <Tooltip 
                                key={index}
                                label={`${member.userprofile_id} (${getMemberRoleLabel(member.role)})`}
                              >
                                <Avatar
                                  size="sm"
                                  name={member.userprofile_id}
                                  bg={member.is_speaking ? 'green.500' : 'gray.500'}
                                  border={member.is_muted ? '2px solid red' : member.is_deafened ? '2px solid orange' : undefined}
                                />
                              </Tooltip>
                            ))}
                          </AvatarGroup>
                          {group.members.members.some(m => m.is_speaking) && (
                            <HStack ml={2}>
                              <FiVolume2 color="green" />
                              <Text fontSize="xs" color="green.500">
                                Speaking
                              </Text>
                            </HStack>
                          )}
                          {group.members.members.some(m => m.is_muted) && (
                            <HStack>
                              <FiVolumeX color="red" />
                              <Text fontSize="xs" color="red.500">
                                Muted
                              </Text>
                            </HStack>
                          )}
                          {group.members.members.some(m => m.is_deafened) && (
                            <HStack>
                              <FiHeadphones color="orange" />
                              <Text fontSize="xs" color="orange.500">
                                Deafened
                              </Text>
                            </HStack>
                          )}
                        </HStack>
                      </VStack>
                    )}
                    
                    {latestStatus && (
                      <Text fontSize="xs" color="blue.500">
                        Last updated: {new Date(latestStatus.timestamp).toLocaleTimeString()}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}

      <CreateGroupModal
        isOpen={isOpen}
        onClose={onClose}
        onGroupCreated={handleGroupCreated}
        roomId={roomId}
      />
    </Box>
  );
};